import { useState } from 'react'
import { useAuth } from '@astro/auth'
import { useToast } from '../../../context/ToastContext'
import { useReviewsAggregate, useReviewsList, useSubmitReview } from '../../../hooks/useReviews'
import { SORT_OPTIONS, RATING_FILTERS } from '../reviews/reviewsData'
import RatingSummary from '../reviews/RatingSummary'
import ReviewCard from '../reviews/ReviewCard'
import ReviewForm from '../reviews/ReviewForm'
import { StarRatingDisplay } from '../reviews/StarRating'

const PER_PAGE = 5

function ReviewSkeleton() {
  return (
    <div className="flex gap-4 animate-pulse">
      <div className="w-11 h-11 rounded-full bg-stone-100 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-40 rounded bg-stone-100" />
        <div className="h-3 w-24 rounded bg-stone-100" />
        <div className="h-3 w-full rounded bg-stone-100" />
        <div className="h-3 w-2/3 rounded bg-stone-100" />
      </div>
    </div>
  )
}

export default function AstrologerReviewsSection({ astro }) {
  const { status: authStatus, login } = useAuth()
  const { showToast } = useToast()

  const [sortBy, setSortBy] = useState('recent')
  const [ratingFilter, setRatingFilter] = useState(0)
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)

  const { summary, myReview, isPending: summaryPending } = useReviewsAggregate('astrologer', astro.id)
  const { items, currentPage, lastPage, total, isPending, isFetching, isError, refetch } = useReviewsList(
    'astrologer', astro.id, { sortBy, rating: ratingFilter, page, perPage: PER_PAGE },
  )
  const submitReview = useSubmitReview('astrologer', astro.id)
  const myReviewPending = summaryPending

  const changeSort = (value) => { setSortBy(value); setPage(1) }
  const changeRatingFilter = (value) => { setRatingFilter(value); setPage(1) }

  const openForm = () => {
    if (authStatus !== 'authenticated') {
      showToast('Please sign in to write a review.')
      login()
      return
    }
    setShowForm(true)
  }

  const handleSubmit = async (payload) => {
    await submitReview.mutateAsync(payload)
    setShowForm(false)
    showToast('Thank you — your review has been posted.')
  }

  return (
    <div className="info-card p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-cinzel text-[#1C140A]">Client reviews</h2>
        {myReviewPending ? null : myReview ? (
          <div className="flex items-center gap-2 text-xs text-[#8B7355]">
            <span className="font-semibold text-[#1C140A]">You reviewed this astrologer</span>
            <StarRatingDisplay value={myReview.rating} size="sm" />
          </div>
        ) : (
          <button
            type="button"
            onClick={openForm}
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-5 py-2.5 rounded-full border transition-colors"
            style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
          >
            ✍️ Write a review
          </button>
        )}
      </div>

      <RatingSummary
        averageRating={summary.averageRating}
        totalReviews={summary.totalReviews}
        breakdown={summary.breakdown}
        breakdownBasis={summary.breakdownBasis}
        loading={summaryPending}
      />

      {summary.totalReviews > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[#D7C29A]/20">
          <div className="flex flex-wrap gap-1.5">
            {RATING_FILTERS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => changeRatingFilter(r)}
                className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${ratingFilter === r
                  ? 'bg-[#1C140A] text-white border-[#1C140A]'
                  : 'text-[#8B7355] border-[#D7C29A]/40 hover:bg-stone-50'
                  }`}
              >
                {r === 0 ? 'All' : `${r}★`}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => changeSort(e.target.value)}
            className="text-xs font-semibold text-[#1C140A] border border-[#D7C29A]/40 rounded-full px-3.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#C8922A]/30"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {isPending ? (
        <div className="space-y-5">
          {Array.from({ length: 3 }, (_, i) => <ReviewSkeleton key={i} />)}
        </div>
      ) : isError ? (
        <div className="text-center py-6">
          <p className="text-sm text-[#8B7355] mb-3">Couldn&rsquo;t load reviews right now.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-[10px] font-bold uppercase tracking-widest px-5 py-2.5 rounded-full border border-[#C8922A]/35 text-[#C8922A]"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#8B7355]">
          {ratingFilter > 0
            ? `No ${ratingFilter}-star reviews yet.`
            : `No reviews yet — be the first to book a session with ${astro.name}.`}
        </p>
      ) : (
        <div className={`space-y-5 transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
          {items.map((review, i) => (
            <div key={review.id ?? i}>
              <ReviewCard review={review} />
              {i < items.length - 1 && <div className="astro-divider mt-5" />}
            </div>
          ))}
        </div>
      )}

      {!isPending && !isError && lastPage > 1 && (
        <div className="flex items-center justify-between pt-2 border-t border-[#D7C29A]/20">
          <button
            type="button"
            disabled={currentPage <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border border-[#D7C29A]/40 text-[#8B7355] disabled:opacity-40"
          >
            ← Previous
          </button>
          <span className="text-xs text-[#8B7355]">Page {currentPage} of {lastPage} · {total} total</span>
          <button
            type="button"
            disabled={currentPage >= lastPage || isFetching}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border border-[#D7C29A]/40 text-[#8B7355] disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      {showForm && (
        <ReviewForm
          astrologerName={astro.name}
          onSubmit={handleSubmit}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  )
}
